import { createSelector } from '@ngrx/store';
import { QuestionnaireState } from '../questionnaire-utils';

export const questionnaireFeatureKey = 'questionnaire';

export const selectActiveQuestionnaireID = createSelector(
  (state: any) => state[questionnaireFeatureKey],
  (questionnaireState: QuestionnaireState) =>
    questionnaireState.activeQuestionnaireId,
);

export const selectQuestionnaire = createSelector(
  (state: any) => state[questionnaireFeatureKey],
  (questionnaireState: QuestionnaireState) => questionnaireState.questionnaire,
);

/**
 * Get the next visible module in the questionnaire after the module with the given ID
 * @param moduleId ID of a module
 * @returns A selector that returns the next module in the questionnaire after the module with the given ID
 */
export const selectNextModule = (moduleId: string) =>
  createSelector(
    (state: any) => state[questionnaireFeatureKey],
    (state: QuestionnaireState) => {
      const module = state.questionnaireState.modules[moduleId];

      if (!module) {
        return null;
      }

      const index = state.questionnaire?.modules.findIndex(
        (m) => m.id === moduleId,
      );

      if (index === undefined || index === -1) {
        return null;
      }

      return state.questionnaire?.modules
        .slice(index + 1)
        .find((m) => state.questionnaireState.modules[m.id]?.visible);
    },
  );

export const selectPreviousModule = (moduleId: string) =>
  createSelector(
    (state: any) => state[questionnaireFeatureKey],
    (questionnaireState: QuestionnaireState) => {
      const module = questionnaireState.questionnaireState.modules[moduleId];

      if (!module) {
        return null;
      }

      const index = questionnaireState.questionnaire?.modules.findIndex(
        (m) => m.id === moduleId,
      );

      if (index === undefined || index === -1) {
        return null;
      }

      return questionnaireState.questionnaire?.modules
        .slice(0, index)
        .reverse()
        .find(
          (m) => questionnaireState.questionnaireState.modules[m.id]?.visible,
        );
    },
  );

export const selectSectionOfQuestionnaire = createSelector(
  (state: any) => state[questionnaireFeatureKey],
  (questionnaireState: QuestionnaireState) => questionnaireState.section,
);

export const selectQuestionDict = createSelector(
  (state: any) => state[questionnaireFeatureKey],
  (questionnaireState: QuestionnaireState) => questionnaireState.questionDict,
);

export const selectQuestionVisibility = (questionId: string) =>
  createSelector(
    (state: any) => state[questionnaireFeatureKey],
    (questionnaireState: QuestionnaireState) =>
      !!questionnaireState.questionnaireState.questions[questionId]?.visible,
  );

export const selectQuestionnaireState = createSelector(
  (state: any) => state[questionnaireFeatureKey],
  (questionnaireState: QuestionnaireState) =>
    questionnaireState.questionnaireState,
);

export const selectModuleAnswerCounts = (moduleId: string) =>
  createSelector(
    (state: any) => state[questionnaireFeatureKey],
    (questionnaireState: QuestionnaireState) =>
      questionnaireState.questionnaireState.modules[moduleId],
  );

export const selectModuleVisibility = (moduleId: string) =>
  createSelector(
    (state: any) => state[questionnaireFeatureKey],
    (questionnaireState: QuestionnaireState) =>
      questionnaireState.questionnaireState.modules[moduleId]?.visible,
  );

export const selectLoadingQuestionnaire = createSelector(
  (state: any) => state[questionnaireFeatureKey],
  (questionnaireState: QuestionnaireState) =>
    questionnaireState.loadingQuestionnaire,
);

export const getSavingModuleMark = (moduleId: string, mark: 'QC' | 'SME') =>
  createSelector(
    (state: any) => state[questionnaireFeatureKey],
    (questionnaireState: QuestionnaireState) =>
      questionnaireState.savingModuleMarks[moduleId]?.[mark],
  );

export const getSavingAnswerMark = (answerId: string, mark: 'QC' | 'SME') =>
  createSelector(
    (state: any) => state[questionnaireFeatureKey],
    (questionnaireState: QuestionnaireState) =>
      questionnaireState.savingAnswerMarks[answerId]?.[mark],
  );

export const getSavingModuleAnswerMarks = (
  moduleId: string,
  mark: 'QC' | 'SME',
) =>
  createSelector(
    (state: any) => state[questionnaireFeatureKey],
    (questionnaireState: QuestionnaireState) => {
      const module = questionnaireState.questionnaireState.modules[moduleId];

      if (!module) {
        return {};
      }

      const questions = module.questions;

      if (!questions) {
        return {};
      }

      return questions.reduce(
        (acc, question) => {
          if (!question.answerId) {
            return acc;
          }
          acc[question.answerId] =
            questionnaireState.savingAnswerMarks[question.answerId]?.[mark];
          return acc;
        },
        {} as Record<string, boolean>,
      );
    },
  );

export const selectLoadingSubmissionTeam = (submissionId: string) =>
  createSelector(
    (state: any) => state[questionnaireFeatureKey],
    (questionnaireState: QuestionnaireState) =>
      questionnaireState.loadingSubmissionTeam[submissionId],
  );

export const selectSubmissionTeam = (submissionId: string) =>
  createSelector(
    (state: any) => state[questionnaireFeatureKey],
    (questionnaireState: QuestionnaireState) =>
      questionnaireState.submissionTeam[submissionId],
  );
